import { AddToCurrentCartFailedError, OptionalStateCarts, StateCart, StateCartEntry } from '@spa/lib/store/carts/CartsState';
import { ChangeCurrentCartSelectedStoreRequestedPayload, addToCurrentCartRequested, changeCurrentCartSelectedStoreRequested } from '@spa/lib/store/carts/cartsSlice';
import { CountryCodes, countryDomains, defaultCartQuantityIncrement, maximumDefaultCartQuantity, minimumDefaultCartQuantity, numberOfFreeSamples } from '@spa/lib/const';
import type { HomeDeliveryDeliveryMode, ProductInfoProduct, ProductInfoProductBrand } from './ProductInfoProductTypes';
import PopupComponent, { PopupType } from '../popup/popup.component';
import { ProductDatasheet, ProductInfoProductAssetType } from '@spa/components/ProductAssetTypes';
import { ReactElement, ReactNode, memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { differenceInDays, format, isAfter, parseISO, startOfDay, subDays } from 'date-fns';
import useLastAddToCurrentCartResult, { LastAddToCurrentCartResponse } from '@spa/lib/react/hooks/useLastAddToCurrentCartResult';
import { useSelector, useStore } from 'react-redux';
import AddToCartComponent from '../add-to-cart/add-to-cart.component';
import ButtonComponent from '../button/button/button.component';
import { ButtonType } from '../button/types/ButtonType';
import { CustomOrderType } from '@spa/lib/store/carts/CustomOrderType';
import { DetailedStore } from '../storefinder/interfaces';
import DynamicAddedToCartLayerComponent from '../add-to-cart/dynamic-added-to-cart-layer.component';
import DynamicProductLegalNoticeComponent from '../product-legal-notice/dynamic-product-legal-notice.component';
import { DynamicProductTestRatingComponent } from '@spa/components/product-test-rating/dynamic-product-test-rating.component';
import FreeSamplesBannerComponent from '../free-samples-banner/free-samples-banner.component';
import ImageComponent from '@spa/components/image-component/image.component';
import IncompatibleCartLayerComponent from '@spa/components/add-to-cart/incompatible-cart-layer.component';
import Link from 'next/link';
import { LocaleContext } from '@spa/lib/react/context/LocaleContext';
import { MutableRefObject } from 'preact/compat';
import { NamespacedIntl } from '@spa/lib/i18n/common/NamespacedIntl';
import { OrderLimits } from '@spa/lib/react/hooks/useOrderLimits';
import { PageTemplate } from '@spa/lib/cms/const';
import PickupInfoComponent from '../storefinder/pickup-info/pickup-info.component';
import ProductAttributeListComponent from '../product-attribute-list/product-attribute-list.component';
import ProductBadgesComponent from '@spa/components/product-info/product-badges/product-badges.component';
import { ProductDeliveryInformation } from './product-delivery-information/product-delivery-information';
import { ProductDeliveryMethod } from './product-delivery-method/product-delivery-method';
import ProductEnergyLabelComponent from '@spa/components/product-energy-label/product-energy-label.component';
import ProductMediaComponent from './product-media/product-media.component';
import ProductPrice from '../product-price/product-price.component';
import ProductRatingsComponent from '../product-ratings/product-ratings.component';
import ReminderButtonComponent from '../reminder-button/reminder-button.component';
import { SessionsData } from '@spa/lib/auth/sessionTypes';
import StickyBuyButtonComponent from '@spa/components/sticky-buy-button/sticky-buy-button.component';
import { StickyBuyRowPosition } from '@spa/components/sticky-buy-button/StickyBuyRowPosition';
import { Store } from '@reduxjs/toolkit';
import { StoreAvailability } from './store-availability/store-availability.component';
import StorefinderComponent from '../storefinder/dynamic-storefinder.component';
import type { ViewItemEventItem } from '@spa/lib/tracking/eCommerceTrackingEventSchema';
import WishlistComponent from '@spa/components/wishlist/wishlist.component';
import { WishlistsActionOrigin } from '@spa/lib/tracking/trackWishlistsActions';
import { addSearchFilters } from '@spa/lib/store/search/searchSlice';
import buildOptimizedImageUrl from '@spa/lib/image-loader/buildOptimizedImageUrl';
import calculatePriceValues from '@spa/lib/getPriceValues';
import classNames from 'clsx';
import { computeDefaultCustomOrderType } from './default-custom-order-type';
import { computeIsProductOnSale } from '@spa/components/product-price/computeIsProductOnSale';
import convertProductToAddedToCartLayerProduct from '@spa/pages-converters/products/convertProductToAddedToCartLayerProduct';
import { convertProductToCommonECommerceEventItem } from '@spa/lib/tracking/convertProductToCommonECommerceEventItem';
import formatPriceWithCurrency from '@spa/lib/formatPriceWithCurrency';
import generateActionId from '@spa/lib/generateActionId';
import getAuthSessionUserId from '@spa/lib/auth/getAuthSessionUserId';
import { getDateFormat } from '@spa/lib/formsSchemas/commonValidations';
import { getInitialQuantityToBuy } from '@spa/lib/getInitialQuantityToBuy';
import { getListContext } from '@spa/lib/tracking/listContextStorage';
import isProductCompatibleWithCarts from '@spa/lib/isProductCompatibleWithCarts';
import { isShopStoreType } from '@spa/lib/configs/storeType';
import { pushScarabCommands } from '@spa/lib/emarsys/pushScarabCommands';
import selectCarts from '@spa/lib/store/carts/selectors/selectCarts';
import selectCountryCode from '@spa/lib/store/settings/selectors/selectCountryCode';
import selectFailedAddToCurrentCartActionIds from '@spa/lib/store/carts/selectors/selectFailedAddToCurrentCartActionIds';
import selectGeneralSettings from '@spa/lib/store/settings/selectors/selectGeneralSettings';
import selectPageTemplate from '@spa/lib/store/router/selectors/selectPageTemplate';
import selectPdpSettings from '@spa/lib/store/settings/selectors/selectPdpSettings';
import selectPricesIncludeTax from '@spa/lib/store/settings/selectors/selectPricesIncludeTax';
import selectPublicBaseUrl from '@spa/lib/store/settings/selectors/selectPublicBaseUrl';
import selectSearchSettings from '@spa/lib/store/settings/selectors/selectSearchSettings';
import selectSearchState from '@spa/lib/store/search/selectors/selectLastQueryMetadata';
import selectSessionLatestSelectedDeliveryMethod from '@spa/lib/store/session/selectors/selectSessionLatestSelectedDeliveryMethod';
import selectSucceededAddToCurrentCartActionResults from '@spa/lib/store/carts/selectors/selectSucceededAddToCurrentCartResults';
import selectWishlistSettings from '@spa/lib/store/settings/selectors/selectWishlistSettings';
import styles from './product-info.component.module.scss';
import { tracker } from '@spa/lib/tracking/tracker';
import updateCsrSession from '@spa/lib/auth/updateCsrSession';
import useNamespacedIntl from '@spa/lib/i18n/client/hooks/useNamespacedIntl';
import useNextAuthOrSession from '@spa/lib/react/hooks/useNextAuthOrSession';
import { usePathname } from 'next/navigation';
import { usePopularityEventsAPI } from '@spa/lib/react/hooks/usePopularityEventsAPI';
import { useSession } from 'next-auth/react';
import { useStoresAPI } from '@spa/lib/react/hooks/useStoresAPI';
const limitedStockLevelThreshold: number = 20;
const DAYS_FOR_RELEASE_DATE_AVAILABLE_ONLINE: number = 1;
const MAX_SCHEMA_DESCRIPTION_LENGTH = 5000;
const stringifyScriptJson = (value: Record<string, unknown>): string => JSON.stringify(value).replace(/</g, '\\u003c');
export interface ProductInfoComponentProps {
  readonly componentId: string;
  readonly homedeliveryDeliveryMode: HomeDeliveryDeliveryMode | null;
  readonly orderLimits: OrderLimits;
  readonly priority: boolean;
  readonly product: ProductInfoProduct;
  readonly searchFilters: ReadonlyArray<string>;
  readonly favoriteStore: DetailedStore | null;
  readonly isMaxQuantityReached: boolean;
}
interface StoreInfo {
  readonly name: string;
  readonly street: string;
  readonly town: string;
  readonly zip: string;
  readonly isValid: boolean;
}
const findActiveBrand = (brand: ProductInfoProductBrand | null, subBrand: ProductInfoProductBrand | null): ProductInfoProductBrand | undefined => {
  let activeBrand: ProductInfoProductBrand | undefined;
  if (subBrand?.image) {
    activeBrand = subBrand;
  } else if (brand?.image) {
    activeBrand = brand;
  } else if (subBrand?.name) {
    activeBrand = subBrand;
  } else if (brand?.name) {
    activeBrand = brand;
  }
  return activeBrand;
};
function ProductInfoComponent({
  componentId,
  homedeliveryDeliveryMode: homeDeliveryDeliveryMode,
  orderLimits,
  priority,
  product,
  searchFilters,
  favoriteStore,
  isMaxQuantityReached
}: ProductInfoComponentProps): ReactElement {
  const {
    formatMessage
  }: NamespacedIntl = useNamespacedIntl('product-info');
  const locale: string = useContext(LocaleContext);
  const store: Store = useStore();
  const publicBaseUrl: string = useSelector(selectPublicBaseUrl);
  const countryCode: string = useSelector(selectCountryCode);
  const shouldHidePrices: boolean = countryCode === CountryCodes.HU;
  const pricesIncludeTax: boolean = useSelector(selectPricesIncludeTax);
  const hasTrackedView: MutableRefObject<boolean> = useRef(false);
  const {
    authSession,
    session
  }: SessionsData = useNextAuthOrSession();
  const lastQueryMetadata: Record<string, string> = useSelector(selectSearchState);
  const [showStoreFinderPopup, setShowStoreFinderPopup] = useState<boolean>(false);
  const [showIncompatibleCartErrorPopup, setShowIncompatibleCartErrorPopup] = useState<boolean>(false);
  const [selectedStore, setSelectedStore] = useState<DetailedStore | null>(favoriteStore);
  const [availableAtSelectedStore, setAvailableAtSelectedStore] = useState<boolean>(false);
  const carts: OptionalStateCarts = useSelector(selectCarts);
  const collectAtStoreCart: StateCart | null = carts.COLLECT_AT_STORE;
  const homeDeliveryCart: StateCart | null = carts.HOME_DELIVERY;
  const [selectedProduct, setSelectedProduct] = useState<ProductInfoProduct>();
  const [showStoreNotSelectedError, setShowStoreNotSelectedError] = useState<boolean>(false);
  const {
    pdpEnabled: isWishlistEnabled
  } = useSelector(selectWishlistSettings);
  const {
    popularityEventsEnabled
  } = useSelector(selectSearchSettings);
  const {
    preOrderEnabled,
    showProductTestEnabled,
    bazaarvoiceEnabled
  } = useSelector(selectGeneralSettings);
  const {
    pdpShowVideosEnabled
  } = useSelector(selectPdpSettings);
  const pageTemplate: PageTemplate = useSelector(selectPageTemplate);
  const {
    sendCartAdditionEvent
  } = usePopularityEventsAPI();
  const pathname: string = usePathname();
  const {
    buyable,
    badges,
    basePrice,
    brand,
    campaignPrice,
    clickAndCollect,
    code,
    currentPrice,
    energyEfficiencyLabel,
    energyEfficiencyOverview,
    ean,
    homeDelivery,
    images,
    manufacturerArticleNumber,
    mobileImages,
    name,
    path,
    preOrderSettings,
    productDataSheets,
    productVideos,
    recommendedRetailPrice,
    releaseDate,
    requiredLegalNotices,
    saleMaximumQuantity,
    saleMinimumQuantity,
    saleQuantityIncrements,
    descriptionHtml,
    stockLevel,
    strikePrice,
    subBrand,
    testRatingAsset,
    testRatingFrontViewAsset
  }: ProductInfoProduct = product;
  const isProductOnSale: boolean = computeIsProductOnSale(countryCode, pricesIncludeTax, {
    currentPrice,
    recommendedRetailPrice,
    campaignPrice,
    strikePrice
  });
  const isPreOrderActive: boolean = !!(preOrderEnabled && preOrderSettings?.isActive);
  const isClickAndCollectAllowed: boolean = !!clickAndCollect && !isPreOrderActive;
  const isClickAndCollectAllowedForStore = selectedStore?.storePickUp ?? true;
  const isHomeDeliveryPossible: boolean = !!homeDelivery && homeDeliveryDeliveryMode !== null;
  const hasNoAvailableDeliveryType: boolean = !(isClickAndCollectAllowed && isClickAndCollectAllowedForStore) && !isHomeDeliveryPossible;
  const isReleased: boolean = !releaseDate || new Date(releaseDate) < new Date();
  const [stickyBuyRowPosition, setStickyBuyRowPosition] = useState<StickyBuyRowPosition | null>(null);
  const latestSelectedDeliveryMethod: CustomOrderType = useSelector(selectSessionLatestSelectedDeliveryMethod);
  const {
    checkoutDeliverySelectionEnabled
  } = useSelector(selectGeneralSettings);
  const isSwitzerland = countryCode === 'CH';
  const initialQuantityToBuy: number = getInitialQuantityToBuy({
    isPreOrderActive,
    saleMinimumQuantity
  });
  const [quantityToBuy, setQuantityToBuy] = useState(initialQuantityToBuy);
  const {
    currentPriceValue
  } = calculatePriceValues(pricesIncludeTax, {
    currentPrice
  });
  const siblingCodes = useMemo(() => product.siblings.map(s => s.code), [product.siblings]);
  const listContext = getListContext(code, siblingCodes);
  const anonymousUserId: string = session.guid;
  const userId: string | null = getAuthSessionUserId(authSession);
  const {
    status: sessionStatus
  } = useSession();
  const {
    getStoreStockIndicatorForProduct
  } = useStoresAPI();
  const schemaDescription: string | undefined = useMemo(() => {
    let result: string | undefined = undefined;
    if (descriptionHtml) {
      const textOnly = descriptionHtml.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
      if (textOnly) {
        result = textOnly.length > MAX_SCHEMA_DESCRIPTION_LENGTH ? textOnly.slice(0, MAX_SCHEMA_DESCRIPTION_LENGTH) : textOnly;
      }
    }
    return result;
  }, [descriptionHtml]);
  const [loadingErrorAvailability, setLoadingErrorAvailability] = useState<boolean>(false);
  useEffect(() => {
    if (!hasTrackedView.current && sessionStatus !== 'loading') {
      const itemQuantity = saleMinimumQuantity ?? 1;
      const commonECommerceEventItem = convertProductToCommonECommerceEventItem({
        product,
        listContext,
        country: countryCode,
        preOrderEnabled,
        pricesIncludeTax
      });
      const viewItemEventItem: ViewItemEventItem = {
        ...commonECommerceEventItem,
        quantity: itemQuantity
      };
      tracker.viewItem({
        anonymousId: anonymousUserId,
        authenticatedId: userId,
        ecommerce: {
          currency: currentPrice.currencyIso,
          value: currentPrice.valueWithoutTax * itemQuantity,
          // Event-level attribution mirrors the single view_item item (AC1/AC4); omitted on miss.
          ...(commonECommerceEventItem.item_list_id ? {
            item_list_id: commonECommerceEventItem.item_list_id
          } : {}),
          ...(commonECommerceEventItem.item_list_name ? {
            item_list_name: commonECommerceEventItem.item_list_name
          } : {}),
          items: [viewItemEventItem]
        }
      });
      hasTrackedView.current = true;
    }
  }, [anonymousUserId, userId, sessionStatus, lastQueryMetadata, product, pathname, pageTemplate, shouldHidePrices, saleMinimumQuantity, currentPrice.currencyIso, currentPrice.valueWithoutTax, listContext, countryCode, preOrderEnabled, pricesIncludeTax]);
  useEffect(() => {
    let cancelled = false;
    const fetchStock = async (): Promise<void> => {
      if (product?.code && selectedStore?.code) {
        try {
          setLoadingErrorAvailability(false);
          const stockData: boolean = await getStoreStockIndicatorForProduct(product.code, selectedStore.code);
          if (!cancelled) {
            setAvailableAtSelectedStore(stockData);
          }
        } catch {
          if (!cancelled) {
            setLoadingErrorAvailability(true);
          }
        }
      }
    };
    void fetchStock();
    return (): void => {
      cancelled = true;
    };
  }, [selectedStore?.code, product?.code, getStoreStockIndicatorForProduct, setAvailableAtSelectedStore]);
  useEffect(() => {
    pushScarabCommands(['view', code], ['go']);
  }, [code]);
  const [customOrderType, setCustomOrderType] = useState<CustomOrderType>((): CustomOrderType => computeDefaultCustomOrderType({
    checkoutDeliverySelectionEnabled,
    hasCollectAtStoreCart: collectAtStoreCart !== null,
    hasHomeDeliveryCart: homeDeliveryCart !== null,
    isClickAndCollectAllowedForStore,
    isHomeDeliveryPossible,
    isPreOrderActive,
    isSwitzerland,
    latestSelectedDeliveryMethod
  }));
  const onStoreSelectedInStorefinder = useCallback((newSelectedStore: DetailedStore | null): void => {
    setShowStoreFinderPopup(false);
    setSelectedStore(newSelectedStore);
    setShowStoreNotSelectedError(false);
    if (newSelectedStore?.storePickUp === false) {
      const resetCustomOrderType: CustomOrderType = computeDefaultCustomOrderType({
        checkoutDeliverySelectionEnabled,
        hasCollectAtStoreCart: collectAtStoreCart !== null,
        hasHomeDeliveryCart: homeDeliveryCart !== null,
        isClickAndCollectAllowedForStore: false,
        isHomeDeliveryPossible,
        isPreOrderActive,
        isSwitzerland,
        latestSelectedDeliveryMethod
      });
      setCustomOrderType(resetCustomOrderType);
      updateCsrSession({
        latestSelectedDeliveryMethod: resetCustomOrderType
      });
    }
    if (newSelectedStore && collectAtStoreCart) {
      store.dispatch(changeCurrentCartSelectedStoreRequested({
        actionId: generateActionId(),
        storeCode: newSelectedStore.code
      } as ChangeCurrentCartSelectedStoreRequestedPayload));
    }
  }, [checkoutDeliverySelectionEnabled, collectAtStoreCart, homeDeliveryCart, isHomeDeliveryPossible, isPreOrderActive, isSwitzerland, latestSelectedDeliveryMethod, store]);
  const [addToCartDialogClosed, setAddToCartDialogClosed] = useState<boolean>(true);
  const [lastActionId, setLastActionId] = useState<number | null>(null);
  const succeededAddToCurrentCartActionIds: ReadonlyArray<number> = useSelector(selectSucceededAddToCurrentCartActionResults).map(actionResult => actionResult.requestedActionId);
  const failedAddToCurrentCartActionIds: Array<number> = useSelector(selectFailedAddToCurrentCartActionIds);
  const isAddingToCart: boolean = lastActionId !== null && addToCartDialogClosed && !succeededAddToCurrentCartActionIds.includes(lastActionId) && !failedAddToCurrentCartActionIds.includes(lastActionId);
  const {
    result: lastAddToCurrentCartResult,
    addRequestedActionId
  }: LastAddToCurrentCartResponse = useLastAddToCurrentCartResult();
  const isAddToCartResultPopulated: boolean = lastAddToCurrentCartResult.succeeded !== null || lastAddToCurrentCartResult.failed && lastAddToCurrentCartResult.failedReason !== AddToCurrentCartFailedError.ITEM_MAXIMUM_QUANTITY_VALIDATION_FAILED;
  const productInfoProductPrice = classNames(styles['product-info__price-box'], {
    [styles['product-info__product-price--out-of-stock']]: stockLevel === 0
  });
  const getMaximumOrderableQuantity = (): number => {
    const isLimitedPreOrder = preOrderEnabled && preOrderSettings?.unlimited === false;
    let maxQuantity = saleMaximumQuantity ?? maximumDefaultCartQuantity;
    if (isMaxQuantityReached) {
      maxQuantity = 0;
    } else if (isLimitedPreOrder) {
      maxQuantity = 1;
    }
    return maxQuantity;
  };
  let jsonLdAvailability: string;
  if (stockLevel === 0) {
    jsonLdAvailability = 'https://schema.org/OutOfStock';
  } else if (stockLevel <= limitedStockLevelThreshold) {
    jsonLdAvailability = 'https://schema.org/LimitedAvailability';
  } else {
    jsonLdAvailability = 'https://schema.org/InStock';
  }
  const handleAddToCart = useCallback((customOrderType_0: CustomOrderType, quantity: number, childSelectedStore?: DetailedStore | null): void => {
    setShowStoreFinderPopup(false);
    const targetStore: DetailedStore | null = childSelectedStore ?? selectedStore;
    const homeDeliveryCartEntries: ReadonlyArray<StateCartEntry> = homeDeliveryCart?.entries ?? [];
    const collectAtStoreCartEntries: ReadonlyArray<StateCartEntry> = collectAtStoreCart?.entries ?? [];
    if (isProductCompatibleWithCarts(homeDeliveryCartEntries, collectAtStoreCartEntries, code, preOrderSettings)) {
      setShowIncompatibleCartErrorPopup(true);
    } else if (customOrderType_0 === CustomOrderType.COLLECT_AT_STORE && !targetStore && !collectAtStoreCart?.deliveryAddress?.storeCode) {
      setShowStoreNotSelectedError(true);
    } else {
      const targetStoreCode = targetStore?.code ?? collectAtStoreCart?.deliveryAddress?.storeCode ?? undefined;
      const actionId = generateActionId();
      setLastActionId(actionId);
      addRequestedActionId(actionId);
      setAddToCartDialogClosed(false);
      store.dispatch(addToCurrentCartRequested({
        actionId,
        customOrderType: customOrderType_0,
        productCode: code,
        quantity,
        storeCode: targetStoreCode,
        tracking: listContext ? {
          itemListId: listContext.originId,
          itemListName: listContext.originType
        } : undefined
      }));
      if (searchFilters?.length) {
        store.dispatch(addSearchFilters({
          filters: [...searchFilters],
          productCode: code
        }));
      }
      if (targetStoreCode && collectAtStoreCart) {
        store.dispatch(changeCurrentCartSelectedStoreRequested({
          actionId: generateActionId(),
          storeCode: targetStoreCode
        } as ChangeCurrentCartSelectedStoreRequestedPayload));
      }
      if (popularityEventsEnabled) {
        void sendCartAdditionEvent({
          doc_id: code,
          user_id: userId ?? anonymousUserId
        });
      }
    }
  }, [selectedStore, preOrderSettings, homeDeliveryCart, collectAtStoreCart, code, addRequestedActionId, store, searchFilters, popularityEventsEnabled, sendCartAdditionEvent, userId, anonymousUserId, listContext]);
  const activeBrand: ProductInfoProductBrand | undefined = findActiveBrand(brand, subBrand);
  const brandOrSubrandHref = subBrand ? `/b${subBrand.normalizedSlug}` : `/b${brand?.normalizedSlug}`;
  const brandSearchPath: string = brandOrSubrandHref ?? `/search/?q=${activeBrand?.path}`;
  const brandHeaderDisplay: ReactElement | undefined = activeBrand && <Link prefetch={false} href={brandOrSubrandHref ?? brandSearchPath} className={styles['product-info__brand']}>
      {activeBrand.image ? <ImageComponent className={styles['product-info__brand-image']} src={activeBrand.image.url} width={activeBrand.image.width} height={activeBrand.image.height} alt={formatMessage('brandAlt', {
      brandName: activeBrand.name
    })} sizes="200px" /> : activeBrand.name}
    </Link>;
  const priceComponent = (compact?: boolean): ReactElement => <ProductPrice basePrice={basePrice} currentPrice={currentPrice} recommendedRetailPrice={recommendedRetailPrice} campaignPrice={campaignPrice} strikePrice={strikePrice} pricesIncludeTax={pricesIncludeTax} locale={locale} country={countryCode} compactMode={compact} dataTestIdContext={'pdp'} />;
  const priceBlock: ReactElement = <section className={productInfoProductPrice} aria-label={formatMessage('productPrice')}>
      {priceComponent()}
    </section>;
  const addToCartComponent = (position?: StickyBuyRowPosition): ReactElement => <AddToCartComponent customOrderType={customOrderType} disabled={hasNoAvailableDeliveryType} isAddingToCart={isAddingToCart} onAddToCartClicked={handleAddToCart} addToCartFailedReason={lastAddToCurrentCartResult.failedReason} productCode={code} productName={name} quantityToBuy={quantityToBuy} saleMinimumQuantity={saleMinimumQuantity && saleMinimumQuantity > 0 ? saleMinimumQuantity : minimumDefaultCartQuantity} saleMaximumQuantity={getMaximumOrderableQuantity()} saleQuantityIncrements={saleQuantityIncrements && saleQuantityIncrements > 0 ? saleQuantityIncrements : defaultCartQuantityIncrement} stockLevel={stockLevel} setQuantityToBuy={setQuantityToBuy} stickyBuyRowPosition={position} setStickyBuyRowPosition={!position ? setStickyBuyRowPosition : null} showStoreNotSelectedError={showStoreNotSelectedError} preOrderSettings={preOrderEnabled ? preOrderSettings : null} />;
  const stickyBuyRow: ReactElement = <StickyBuyButtonComponent productImage={images ? images[0] : null} productDisplayName={name} productPriceComponent={shouldHidePrices ? <></> : priceComponent(true)} addToCartComponent={addToCartComponent} position={stickyBuyRowPosition} />;
  const energyLabelDatasheet: ProductDatasheet | null = productDataSheets?.find(({
    type
  }) => type === ProductInfoProductAssetType.ENERGY_EFFICIENCY_DATASHEET) ?? null;
  const remainingPreOrderDeliveryTimeBlock = (preOrderReleaseDate: string): ReactNode => {
    const daysLeft: number = differenceInDays(startOfDay(new Date(preOrderReleaseDate)), startOfDay(new Date()));
    return <span className={classNames('text-lg-immutable', 'bold')}>
        {formatMessage(`remainingPreOrderDeliveryTime.${daysLeft === 1 ? 'singular' : 'plural'}`, {
        daysLeft
      })}
      </span>;
  };
  const priceAndLabelsBlock: ReactElement = <div className={styles['product-info__price-and-labels']}>
      {!shouldHidePrices && priceBlock}
      {preOrderEnabled && preOrderSettings?.isActive && remainingPreOrderDeliveryTimeBlock(releaseDate ?? preOrderSettings.validTo)}
      {energyEfficiencyLabel && <ProductEnergyLabelComponent efficiencyClass={energyEfficiencyLabel} overview={energyEfficiencyOverview} datasheet={energyLabelDatasheet} />}
    </div>;
  const storeInfo: StoreInfo = useMemo(() => {
    let info: StoreInfo = {
      name: '',
      street: '',
      town: '',
      zip: '',
      isValid: false
    };
    if (selectedStore) {
      const {
        name: name_0,
        address: {
          street,
          town,
          zip
        }
      } = selectedStore;
      info = {
        name: name_0,
        street,
        town,
        zip,
        isValid: Boolean(name_0 && street && town && zip)
      };
    } else if (collectAtStoreCart?.deliveryAddress) {
      const {
        company,
        line1,
        line2,
        town: town_0,
        postalCode: zip_0
      } = collectAtStoreCart.deliveryAddress;
      info = {
        name: company ?? '',
        street: `${line1} ${line2}`.trim(),
        town: town_0,
        zip: zip_0,
        isValid: Boolean(name && line1 && town_0 && zip_0)
      };
    }
    return info;
  }, [selectedStore, collectAtStoreCart?.deliveryAddress, name]);
  let formattedReleaseDate: string | null = null;
  let formattedReleaseDateOnline: string | null = null;
  let isUnavailableDueToReleaseDate: boolean = releaseDate === undefined;
  if (releaseDate) {
    const dateFormat: string = getDateFormat(countryCode);
    const parsedReleaseDate: Date = new Date(releaseDate);
    const availableOnlineDate: Date = subDays(parsedReleaseDate, DAYS_FOR_RELEASE_DATE_AVAILABLE_ONLINE);
    const today = startOfDay(new Date());
    formattedReleaseDate = format(startOfDay(parsedReleaseDate), dateFormat);
    if (preOrderEnabled && preOrderSettings?.validFrom) {
      const preOrderStart = startOfDay(parseISO(preOrderSettings.validFrom));
      formattedReleaseDateOnline = preOrderStart > today ? format(preOrderStart, dateFormat) : format(startOfDay(availableOnlineDate), dateFormat);
    } else {
      formattedReleaseDateOnline = format(startOfDay(availableOnlineDate), dateFormat);
    }
    if (preOrderEnabled && preOrderSettings?.isActive) {
      isUnavailableDueToReleaseDate = false;
    } else {
      isUnavailableDueToReleaseDate = isAfter(startOfDay(availableOnlineDate), today);
    }
  }
  const hasMerchantReturnPolicy = useMemo(() => {
    return countryCode === 'DE' || countryCode === 'AT' ? {
      '@type': 'MerchantReturnPolicy',
      applicableCountry: countryCode,
      returnPolicyCountry: countryCode,
      returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
      merchantReturnDays: 14,
      itemCondition: 'https://schema.org/NewCondition',
      returnMethod: 'https://schema.org/ReturnByMail',
      returnFees: 'https://schema.org/FreeReturn',
      refundType: 'https://schema.org/FullRefund',
      merchantReturnLink: `https://www.${countryDomains[countryCode.toLowerCase()]}/service/lieferung-retoure/`
    } : undefined;
  }, [countryCode]);
  const shippingDetails = useMemo(() => {
    return (countryCode === 'DE' || countryCode === 'AT') && homeDelivery ? {
      '@type': 'OfferShippingDetails',
      shippingRate: {
        '@type': 'MonetaryAmount',
        value: 3.95,
        currency: 'EUR'
      },
      shippingDestination: {
        '@type': 'DefinedRegion',
        addressCountry: countryCode
      },
      deliveryTime: {
        '@type': 'ShippingDeliveryTime',
        transitTime: {
          '@type': 'QuantitativeValue',
          minValue: 2,
          maxValue: 5,
          unitCode: 'DAY'
        }
      }
    } : undefined;
  }, [countryCode, homeDelivery]);
  const productJsonLdImages: Array<string> = images !== null ? images.map(img => buildOptimizedImageUrl(img.url)) : [];
  const productJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name,
    image: productJsonLdImages,
    description: schemaDescription,
    brand: brand?.name,
    manufacturer: brand?.name ? {
      '@type': 'Organization',
      name: brand.name
    } : undefined,
    offers: shouldHidePrices ? undefined : [{
      '@type': 'Offer',
      price: currentPriceValue,
      priceCurrency: currentPrice.currencyIso,
      itemCondition: 'https://schema.org/NewCondition',
      availability: jsonLdAvailability,
      url: `${publicBaseUrl}${path}`,
      seller: {
        '@type': 'Organization',
        name: formatMessage('sellerName')
      },
      shippingDetails
    }],
    sku: code,
    gtin: ean,
    mpn: manufacturerArticleNumber ?? undefined,
    hasMerchantReturnPolicy
  };
  const showBazaarVoiceRating = bazaarvoiceEnabled && (product.reviewCount ?? 0) > 0;
  const handleProductDeliveryMethodSelect = (customOrderType_1: CustomOrderType): void => {
    setCustomOrderType(customOrderType_1);
    if (customOrderType_1 === CustomOrderType.HOME_DELIVERY) {
      setShowStoreNotSelectedError(false);
    }
  };
  return <>
      <article className={styles['product-info']}>
        <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: stringifyScriptJson(productJsonLd)
      }} />
        {images !== null && images.length > 0 && <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: stringifyScriptJson({
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          url: `${publicBaseUrl}${path}`,
          primaryImageOfPage: {
            '@type': 'ImageObject',
            contentUrl: buildOptimizedImageUrl(images[0].url)
          }
        })
      }} />}

        {showIncompatibleCartErrorPopup && <IncompatibleCartLayerComponent isOpen={showIncompatibleCartErrorPopup} onClosed={(): void => setShowIncompatibleCartErrorPopup(false)} />}
        {brandHeaderDisplay}
        <div className={classNames(styles['product-info__article-nr-mobile'], 'text-base')}>
          {formatMessage('articleNumberLabel')} {code}
        </div>

        <div className={styles['product-info__image-wrapper']}>
          <div className={styles['product-info__badges-wrapper']}>
            {showProductTestEnabled && testRatingAsset && <div className={styles['product-info__test-rating-wrapper']}>
                <DynamicProductTestRatingComponent variant="product-media" source={testRatingAsset.source} alt={testRatingAsset.description ?? testRatingAsset.name ?? ''} />
              </div>}
            <ProductBadgesComponent badges={badges} isProductOnSale={isProductOnSale} isPreOrderActive={isPreOrderActive} />
          </div>
          <ProductMediaComponent id={`${componentId}-media`} images={images} mobileImages={mobileImages} cursorFollowingTooltip={formatMessage('clickToZoomIn')} priority={priority} productName={name} productVideos={pdpShowVideosEnabled ? productVideos : []} testRatingFrontViewAsset={showProductTestEnabled ? testRatingFrontViewAsset : null} />
          {isWishlistEnabled && <div className={styles['product-info__wishlist-wrapper']}>
              <WishlistComponent afterAuthCallbackUrl={path} product={product} quantity={initialQuantityToBuy} actionOrigin={{
            originId: componentId,
            originType: WishlistsActionOrigin.PDP
          }} listAttribution={listContext ? {
            itemListId: listContext.originId,
            itemListName: listContext.originType
          } : undefined} />
            </div>}
        </div>

        <div className={styles['product-info__details-wrapper']}>
          <h1 className={styles['product-info__title']}>{name}</h1>

          <div className={styles['product-info__itemnumber-rating-quantity-and-price-wrapper-desktop']}>
            <div className={styles['product-info__itemnumber-rating-quantity-wrapper']}>
              <div>
                {formatMessage('articleNumberLabel')} {code}
              </div>

              {showBazaarVoiceRating && <ProductRatingsComponent averageRating={product.averageRating} reviewCount={product.reviewCount} enableReviewNavigation={true} />}

              <ProductAttributeListComponent product={product} />
            </div>

            <div className={styles['product-info__price-box-wrapper']}>
              {!isUnavailableDueToReleaseDate && priceAndLabelsBlock}
            </div>
          </div>

          <div className={classNames(styles['product-info__price-rating-and-quantity-wrapper-mobile'])}>
            <div className={styles['product-info__price-box-wrapper']}>
              {!isUnavailableDueToReleaseDate && priceAndLabelsBlock}
            </div>
            {showBazaarVoiceRating && <div className={styles['product-info__ratings-wrapper-mobile']}>
                <ProductRatingsComponent averageRating={product.averageRating} reviewCount={product.reviewCount} enableReviewNavigation={true} />
              </div>}

            <div className={classNames({
            [styles['product-info__attributes-wrapper-mobile']]: !bazaarvoiceEnabled || bazaarvoiceEnabled && (product.reviewCount ?? 0) === 0
          })}>
              <ProductAttributeListComponent product={product} />
            </div>
          </div>

          {isUnavailableDueToReleaseDate ? <div className={classNames(styles['product-info__release-date-message'])}>
              {formatMessage(preOrderEnabled && preOrderSettings?.validFrom ? 'releaseDateUnavailablePreOrder' : 'releaseDateUnavailable', {
            releaseDate: formattedReleaseDate,
            releaseDateOnline: formattedReleaseDateOnline
          })}
            </div> : <>
              {buyable && stockLevel > 0 ? <>
                  <div className={styles['product-info__delivery-wrapper']}>
                    {checkoutDeliverySelectionEnabled ? <ProductDeliveryInformation isHomeDeliveryAllowed={homeDelivery ?? false} homeDeliveryDeliveryMode={homeDeliveryDeliveryMode} isClickAndCollectAllowed={isClickAndCollectAllowed} isPreOrderEnabled={preOrderEnabled} preOrderSettings={preOrderSettings} releaseDate={releaseDate} selectedStore={selectedStore} setSelectedProduct={setSelectedProduct} setShowStoreFinderPopup={setShowStoreFinderPopup} /> : <>
                        {!isHomeDeliveryPossible && !isSwitzerland && <span className={styles['product-info__delivery-unavailable-home']}>
                            {formatMessage('deliveryUnavailableHome')}
                          </span>}

                        <div className={styles['product-info__delivery']}>
                          {homeDeliveryDeliveryMode && <ProductDeliveryMethod customOrderTypeToRender={CustomOrderType.HOME_DELIVERY} customOrderTypeAllowed={isHomeDeliveryPossible} isSelected={customOrderType === CustomOrderType.HOME_DELIVERY} onSelect={handleProductDeliveryMethodSelect} title={formatMessage('deliveryTitleHome')}>
                              <ul>
                                <li>
                                  {preOrderEnabled && preOrderSettings?.isActive ? <span dangerouslySetInnerHTML={{
                          __html: formatMessage('deliveryTimePreOrder', {
                            time: format(parseISO(releaseDate ?? preOrderSettings.validTo), 'dd.MM.yyyy')
                          })
                        }} /> : formatMessage('deliveryTime', {
                          time: homeDeliveryDeliveryMode.description ?? ''
                        })}
                                </li>
                                {homeDeliveryDeliveryMode.freeAbove && <li>
                                    {formatMessage('freeDeliveryFrom', {
                          from: formatPriceWithCurrency(homeDeliveryDeliveryMode.freeAbove.valueWithTax, homeDeliveryDeliveryMode.freeAbove.currencyIso, locale)
                        })}
                                  </li>}
                              </ul>
                            </ProductDeliveryMethod>}

                          <ProductDeliveryMethod customOrderTypeToRender={CustomOrderType.COLLECT_AT_STORE} customOrderTypeAllowed={isClickAndCollectAllowed && isClickAndCollectAllowedForStore} isSelected={customOrderType === CustomOrderType.COLLECT_AT_STORE} onSelect={handleProductDeliveryMethodSelect} title={formatMessage('deliveryTitleStore')}>
                            {isClickAndCollectAllowedForStore ? <ul className={styles['product-info__store-delivery']}>
                                <li className={classNames(styles['product-info__delivery--highlight'])}>
                                  {selectedStore?.pickupTime && isReleased ? <PickupInfoComponent bold={false} time={selectedStore.pickupTime} showIcon={false} /> : formatMessage('freeDelivery')}
                                </li>
                                <li>{formatMessage('paymentUponPickup')}</li>
                              </ul> : <ul className={styles['product-info__store-delivery']}>
                                <li className={classNames(styles['product-info__delivery--warning'])}>
                                  {formatMessage('deliveryUnavailableStore')}
                                </li>
                              </ul>}
                            <div className={styles['product-info__selected-store']}>
                              {storeInfo.isValid && <>
                                  <span className="bold">{storeInfo.name}</span>
                                  <span>{`${storeInfo.street}, ${storeInfo.town} ${storeInfo.zip}`}</span>
                                </>}
                              <ButtonComponent buttonType={ButtonType.AS_TEXT} disabled={!isClickAndCollectAllowed} text={formatMessage(selectedStore ? 'changeStoreLinkText' : 'selectStoreLinkText')} onClick={() => {
                        setSelectedProduct(undefined);
                        setShowStoreFinderPopup(true);
                      }} />
                            </div>
                          </ProductDeliveryMethod>
                        </div>
                      </>}
                    {!checkoutDeliverySelectionEnabled && !isPreOrderActive && isReleased && <StoreAvailability setSelectedProduct={setSelectedProduct} setShowStoreFinderPopup={setShowStoreFinderPopup} product={product} availableAtSelectedStore={availableAtSelectedStore} loadingErrorAvailability={loadingErrorAvailability} selectedStore={selectedStore ?? undefined} />}
                  </div>

                  {customOrderType === CustomOrderType.HOME_DELIVERY && product.hasFreeSamples && <FreeSamplesBannerComponent className={styles['product-info__free-samples-banner']} numberOfSamples={numberOfFreeSamples} />}
                  {isShopStoreType && addToCartComponent()}
                  {checkoutDeliverySelectionEnabled && !isPreOrderActive && isReleased && <StoreAvailability setSelectedProduct={setSelectedProduct} setShowStoreFinderPopup={setShowStoreFinderPopup} product={product} availableAtSelectedStore={availableAtSelectedStore} loadingErrorAvailability={loadingErrorAvailability} selectedStore={selectedStore ?? undefined} />}
                  {isShopStoreType && stickyBuyRow}
                </> : <>
                  {isShopStoreType && <ReminderButtonComponent disabled={true} buttonType={ButtonType.SECONDARY} product={product} aria-label={formatMessage('notDeliverable')} text={formatMessage('notDeliverable')} />}
                  {!isPreOrderActive && isReleased && <StoreAvailability setSelectedProduct={setSelectedProduct} setShowStoreFinderPopup={setShowStoreFinderPopup} product={product} availableAtSelectedStore={availableAtSelectedStore} loadingErrorAvailability={loadingErrorAvailability} selectedStore={selectedStore ?? undefined} />}
                </>}
            </>}
          {isAddToCartResultPopulated && <DynamicAddedToCartLayerComponent onClosed={(): void => {
          setAddToCartDialogClosed(true);
        }} addToCurrentCartResult={lastAddToCurrentCartResult} isOpen={!addToCartDialogClosed} product={convertProductToAddedToCartLayerProduct(product)} orderLimits={orderLimits} />}
        </div>

        {requiredLegalNotices?.map(noticeType => <DynamicProductLegalNoticeComponent key={`${product.code}-${noticeType}`} noticeType={noticeType} />)}
      </article>

      {showStoreFinderPopup && <PopupComponent isOpen={showStoreFinderPopup} onClosed={() => setShowStoreFinderPopup(false)} popupType={PopupType.STOREFINDER} closable={true}>
          <StorefinderComponent favoriteStore={selectedStore} id={`${componentId}-storefinder`} product={selectedProduct} stores={[]} selectedStoreCode={selectedStore?.code ?? collectAtStoreCart?.deliveryAddress?.storeCode ?? null} setSelectedStore={onStoreSelectedInStorefinder} />
        </PopupComponent>}
    </>;
}
export default memo(ProductInfoComponent);